import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { PageHeaderHostContext } from '../pageHeaderContext';

// 与原生端共用紧凑导航高度，避免折叠终点与滚动距离使用不同基准。
const TOOLBAR_HEIGHT = 48;
const EXPANDED_TOP = TOOLBAR_HEIGHT + 16;
const TITLE_LINE_HEIGHT = 34 * 1.1;
const SUBTITLE_LINE_HEIGHT = 16;

/** 以内容的最低可读高度托住正文，避免异步字体换行时正文侵入折叠头部。 */
function minimumExpandedHeight({ eyebrow, subtitle }) {
  return EXPANDED_TOP
    + (eyebrow ? 18 : 0)
    + TITLE_LINE_HEIGHT
    + (subtitle ? 12 + SUBTITLE_LINE_HEIGHT * 2 : 0)
    + 16;
}

/** 返回、会话和菜单固定在导航行中，折叠过程中不重新分配位置。 */
function HeaderToolbar({ title, showTitle = true }) {
  const { leading, actions = [] } = useContext(PageHeaderHostContext);
  return <div style={{ height: TOOLBAR_HEIGHT, display: 'flex', alignItems: 'center', paddingRight: 12 }}>
    <div style={{ width: 64, height: TOOLBAR_HEIGHT, flexShrink: 0, paddingLeft: 12, paddingTop: 6, boxSizing: 'border-box' }}>{leading}</div>
    <div style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {showTitle ? title : null}
    </div>
    {actions.length > 0 && <details style={{ position: 'relative', width: 44, zIndex: 4 }}>
      <summary aria-label="More actions" style={{ cursor: 'pointer', listStyle: 'none', textAlign: 'center', fontSize: 24 }}>···</summary>
      <div style={{ position: 'absolute', right: 0, top: 36, minWidth: 180, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 6, boxShadow: 'var(--shadow-2)' }}>
        {actions.map((action, i) => <button key={i} type="button" disabled={!action.onClick}
          onClick={(event) => { event.currentTarget.closest('details').open = false; action.onClick?.(); }}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: 12, border: 0, background: 'transparent', color: 'var(--text)', cursor: 'pointer' }}>{action.label}</button>)}
      </div>
    </details>}
  </div>;
}

/** 固定标题栏本身不参与正文滚动。 */
export function FixedHeader({ title }) {
  return <header data-testid="fixed-header" style={{ flexShrink: 0, background: 'var(--bg)' }}>
    <HeaderToolbar title={title} />
  </header>;
}

/** 自定义内容占据整个顶部，宿主返回在最高一层，空白区域不拦截自定义交互。 */
export function CustomHeader({ safeArea = true, children }) {
  const { leading, actions = [], safeTop = 0 } = useContext(PageHeaderHostContext);
  return <header data-testid="custom-header" data-safe-area={safeArea} style={{ flexShrink: 0, position: 'relative', minHeight: safeTop + TOOLBAR_HEIGHT }}>
    <div style={{ paddingTop: safeArea ? safeTop : 0 }}>{children}</div>
    <div style={{ position: 'absolute', left: 12, top: safeTop + 6, zIndex: 3 }}>{leading}</div>
    {actions.length > 0 && <div style={{ position: 'absolute', right: 0, top: safeTop, zIndex: 3 }}>
      <PageHeaderHostContext.Provider value={{ actions }}><HeaderToolbar showTitle={false} /></PageHeaderHostContext.Provider>
    </div>}
  </header>;
}

/** 单个标题由滚动进度连续缩放、位移，辅助文案先淡出。 */
export function CollapsingHeader({ title, eyebrow, subtitle, progress, expandedHeight, headRef }) {
  const { actions = [] } = useContext(PageHeaderHostContext);
  const scale = 1 - progress * 0.5;
  const expandedY = EXPANDED_TOP + (eyebrow ? 18 : 0);
  // 与顶部偏移 6、直径 36 的返回键共用中心线。
  const collapsedY = 6 + 36 / 2 - (17 * 1.1) / 2;
  return <header data-testid="collapsing-header" style={{ height: expandedHeight, position: 'sticky', top: TOOLBAR_HEIGHT - expandedHeight, zIndex: 2, background: 'var(--bg)' }}>
    <div style={{ position: 'absolute', top: progress * (expandedHeight - TOOLBAR_HEIGHT), left: 0, right: 0 }}>
      <HeaderToolbar title={title} showTitle={false} />
    </div>
    <div ref={headRef} data-testid="collapsing-header-measure" style={{ boxSizing: 'border-box', padding: '16px 16px 16px', paddingTop: EXPANDED_TOP, visibility: 'hidden', pointerEvents: 'none' }} aria-hidden="true">
      {eyebrow && <div style={{ height: 18 }}>{eyebrow}</div>}
      <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{title}</div>
      {subtitle && <p style={{ margin: '12px 0 0', maxWidth: 280, fontSize: 'var(--t-sm)', lineHeight: 1.1 }}>{subtitle}</p>}
    </div>
    <div style={{ position: 'absolute', top: expandedY + progress * (expandedHeight - TOOLBAR_HEIGHT + collapsedY - expandedY), left: 16 + 48 * progress,
      right: 16 + progress * ((actions.length ? 56 : 12) - 16), overflow: 'hidden', height: progress === 1 ? 22 : undefined }}>
      <h1 style={{ margin: 0, fontSize: 34, lineHeight: 1.1, fontWeight: 800, transform: `scale(${scale})`, transformOrigin: 'top left',
        maskImage: `linear-gradient(#000 37.4px, rgba(0,0,0,${Math.max(0, 1 - progress * 2.5)}) 37.4px)`,
        width: `${100 / scale}%`, display: '-webkit-box', WebkitLineClamp: progress === 1 ? 1 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{title}</h1>
    </div>
    <div aria-hidden={progress >= 0.4} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: Math.max(0, 1 - progress * 2.5), padding: `${EXPANDED_TOP}px 16px 16px` }}>
      {eyebrow && <div style={{ fontSize: 'var(--t-xs)', height: 18, color: 'var(--text-3)' }}>{eyebrow}</div>}
      <div style={{ visibility: 'hidden', fontSize: 34, fontWeight: 800, lineHeight: 1.1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{title}</div>
      {subtitle && <p style={{ margin: '12px 0 0', maxWidth: 280, fontSize: 'var(--t-sm)', lineHeight: 1.1, color: 'var(--text-3)' }}>{subtitle}</p>}
    </div>
    {progress === 1 && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 0.5, background: 'var(--line-soft)' }} />}
  </header>;
}

/** 新模式独占一个滚动容器；底部操作条以真实高度占位，数据更新不重建滚动节点。 */
export function PageWithHeader({ style, title, eyebrow, subtitle, actions, actionBar, customHeader, headerSafeArea = true, horizontalInset = 16, onScroller = null, tabBar = null, children }) {
  const scroller = useRef(null);
  const head = useRef(null);
  const minimumHeight = minimumExpandedHeight({ eyebrow, subtitle });
  const [expandedHeight, setExpandedHeight] = useState(minimumHeight);
  const [offset, setOffset] = useState(0);
  useLayoutEffect(() => {
    if (!head.current) return undefined;
    // 同时保留换行内容和最低高度，且只使用布局像素，避免 Studio 缩放后测量变小。
    const update = () => {
      const measure = head.current;
      if (!measure) return;
      setExpandedHeight(Math.max(minimumHeight, measure.scrollHeight, measure.offsetHeight));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(head.current);
    // 字体加载会改变换行却不一定改变容器宽度，因此在字体就绪事件后再量一次。
    let active = true;
    document.fonts?.ready?.then(() => { if (active) update(); });
    return () => { active = false; observer.disconnect(); };
  }, [minimumHeight, title, eyebrow, subtitle, style]);
  const fixed = style === 'fixed';
  const custom = style === 'custom';
  const progress = Math.min(1, Math.max(0, offset / (expandedHeight - TOOLBAR_HEIGHT)));
  return <div data-header-style={style} style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg)', color: 'var(--text)' }}>
    {fixed && <FixedHeader title={title} />}
    {custom && <CustomHeader safeArea={headerSafeArea}>{customHeader}</CustomHeader>}
    <div ref={(el) => { scroller.current = el; onScroller?.(el); }} data-testid="header-scroll" onScroll={(event) => setOffset(event.currentTarget.scrollTop)}
      style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', overscrollBehavior: 'contain' }}>
      {!fixed && !custom && <CollapsingHeader title={title} eyebrow={eyebrow} subtitle={subtitle} progress={progress} expandedHeight={expandedHeight} headRef={head} />}
      {/* 折叠头部已提供底部 16px 留白，正文只在其余模式保留顶部间距。 */}
      <div style={{ padding: `${fixed || custom ? 16 : 0}px ${horizontalInset}px 48px`, display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        {fixed && eyebrow && <div style={{ paddingInline: 16 - horizontalInset, color: 'var(--text-3)', fontSize: 'var(--t-xs)' }}>{eyebrow}</div>}
        {fixed && subtitle && <p style={{ paddingInline: 16 - horizontalInset, margin: 0, color: 'var(--text-3)', fontSize: 'var(--t-sm)' }}>{subtitle}</p>}
        {actions.length > 0 && <div style={{ paddingInline: 16 - horizontalInset, display: 'flex', flexWrap: 'wrap', gap: 8 }}>{actions.map((c) => c.element)}</div>}
        {children}
      </div>
    </div>
    {actionBar.length > 0 && <div style={{ flexShrink: 0, padding: 16, borderTop: '1px solid var(--line-soft)' }}>{actionBar.map((c) => c.element)}</div>}
    {tabBar}
  </div>;
}
